<template>
  <div>
    <Select
      v-if="canChangeRole"
      :model-value="member.role"
      :disabled="isUpdating"
      @update:model-value="handleRoleChange"
    >
      <SelectTrigger class="w-32 h-8 text-xs">
        <SelectValue>
          <div class="flex items-center">
            <ShieldUser v-if="member.role === 'ADMIN'" class="h-3 w-3 mr-1" />
            <User v-else class="h-3 w-3 mr-1" />
            {{ t(`settings.roles.${member.role.toLowerCase()}`) }}
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ADMIN">
          <div class="flex items-center">
            <ShieldUser class="h-3 w-3 mr-2" />
            {{ t("settings.roles.admin") }}
          </div>
        </SelectItem>
        <SelectItem value="MEMBER">
          <div class="flex items-center">
            <User class="h-3 w-3 mr-2" />
            {{ t("settings.roles.member") }}
          </div>
        </SelectItem>
      </SelectContent>
    </Select>

    <Badge
      v-else
      :variant="member.role === 'ADMIN' ? 'default' : 'outline'"
      class="text-xs"
    >
      <ShieldUser v-if="member.role === 'ADMIN'" class="h-3 w-3 mr-1" />
      <User v-else class="h-3 w-3 mr-1" />
      {{ t(`settings.roles.${member.role.toLowerCase()}`) }}
    </Badge>
  </div>
</template>

<script setup lang="ts">
import { updateMemberRole } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import type { ApiOrganizationMember } from "~/server/api/organizations/[organizationId]/members/index.get";
import type { AcceptableValue } from "reka-ui";
import { ShieldUser, User } from "lucide-vue-next";

interface Props {
  member: ApiOrganizationMember;
  canChangeRole: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  "role-changed": [member: ApiOrganizationMember];
}>();

const isUpdating = ref(false);
const { t } = useI18n();

const handleRoleChange = async (newRole: AcceptableValue) => {
  if (
    !newRole ||
    (newRole !== "ADMIN" && newRole !== "MEMBER") ||
    newRole === props.member.role
  ) {
    return;
  }

  isUpdating.value = true;

  try {
    const updatedMember = await updateMemberRole(
      props.member.id.toString(),
      newRole as "ADMIN" | "MEMBER"
    );
    emit("role-changed", updatedMember);

    // Show success toast
    successToast({
      title: t("settings.memberRoleUpdate.success.title"),
      description: t("settings.memberRoleUpdate.success.description", {
        name: props.member.name || props.member.email,
        role: t(`settings.roles.${newRole.toLowerCase()}`)
      })
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to update member role:", error);

    // Show error toast
    errorToast({
      title: t("settings.memberRoleUpdate.error.title"),
      description:
        error.statusMessage || t("settings.memberRoleUpdate.error.description")
    });
  } finally {
    isUpdating.value = false;
  }
};
</script>
